/**
 * @author       julian@zumper.com (Julian Napolitan)
 * @copyright    Copyright (c) 2020, Zumper
 * @description  ListItem thunks
 */

import { getTraktorEntityId } from '@zcode-ui/web-zumper-integrations'
import { PROP_NAMES } from '@zcode/mixpanel'
import { Origin } from '@zcode/nr-utils'
import { LISTABLES_API_PAGE_LIMIT } from '@zcode/nr/es/shared/constants/list'
import type { Listable } from '@zcode/state-type'
import { debounce } from '@zcode/utils'
import type { Dispatch } from 'redux'
import { selectFirstPageCount } from '../../currentSearch/selectors/index'
import { getFormattedOrigin } from '../../mixpanel/helpers/getFormattedOrigin'
import { mixpanelViewSection } from '../../mixpanel/thunks/mixpanelViewSection'
import type {
  CURRENT_SEARCH_VIEW,
  DETAIL_SIMILAR,
  FAVORITES,
  MESSAGED,
  NEARBY,
  SAVED_SEARCHES,
} from '../../traktor/constants'
import { trackListItemImpressionEvent } from '../../traktor/thunks/trackListItemImpressionEvent'
import type { GetState } from '../../types'

const idsOnScreen = new Set<ReturnType<typeof getTraktorEntityId>>()
const indiciesOnScreen = new Set<number>()
const filterAdsOnScreen = new Set<ReturnType<typeof getTraktorEntityId>>()
const topPickIdsOnScreen = new Set<ReturnType<typeof getTraktorEntityId>>()

const batchTrackViewedListItems = debounce(({ dispatch, listName, page, index, entity }) => {
  const visiblePropertyIds = idsOnScreen.size ? Array.from(idsOnScreen) : undefined
  const visiblePropertyIndicies = indiciesOnScreen.size ? Array.from(indiciesOnScreen) : undefined
  const visibleFilterAdIndices = filterAdsOnScreen.size ? Array.from(filterAdsOnScreen) : undefined
  const visibleTopPickPropertyIds = topPickIdsOnScreen.size
    ? Array.from(topPickIdsOnScreen)
    : undefined

  idsOnScreen.clear()
  indiciesOnScreen.clear()
  filterAdsOnScreen.clear()
  topPickIdsOnScreen.clear()

  visiblePropertyIds &&
    visiblePropertyIndicies &&
    dispatch(
      trackListItemImpressionEvent({
        listName,
        pageNumber: page,
        visibleFilterAdIndices,
        visiblePropertyIds,
        visiblePropertyIndicies,
        visibleTopPickPropertyIds,
      })
    )
  if (entity?.isFilterAd) {
    dispatch(
      mixpanelViewSection(getFormattedOrigin(Origin.LIST_ITEM), {
        [PROP_NAMES.IS_FILTER_AD]: entity?.isFilterAd,
        [PROP_NAMES.MAPLIST_CARD_INDEX]: index,
      })
    )
  }
}, 500)

interface Props {
  entity: Listable
  index: number
  isTopPick?: boolean
  listName:
    | 'forYouTopPicks'
    | typeof MESSAGED
    | typeof NEARBY
    | typeof CURRENT_SEARCH_VIEW
    | typeof DETAIL_SIMILAR
    | typeof SAVED_SEARCHES
    | typeof FAVORITES
    | undefined
  page: number
}

export const trackListItemImpression =
  ({ entity, index, isTopPick, listName, page }: Props) =>
  (dispatch: Dispatch, getState: GetState) => {
    const state = getState()
    const firstPageCount = selectFirstPageCount(state)
    const traktorIndex =
      page === 1 ? index : (firstPageCount ?? 0) + LISTABLES_API_PAGE_LIMIT * (page - 2) + index
    const traktorEntityId = getTraktorEntityId(entity)
    idsOnScreen.add(traktorEntityId)
    entity?.isFilterAd && filterAdsOnScreen.add(traktorEntityId)
    indiciesOnScreen.add(traktorIndex)
    isTopPick && topPickIdsOnScreen.add(traktorEntityId)
    batchTrackViewedListItems({ dispatch, listName, page, index, isTopPick, entity })
  }
